import React, { useMemo, useState } from 'react';
import {
  Button, Icon, SearchInput, SegmentedControl, Panel, Avatar, ScoreChip, StagePill, BatchPill, ResultStatus,
  FamilyIcon, Menu, MenuItem, STAGES, fmtScore, scoreTone, useToast,
} from '../../components/ui';
import { FAMILIES, typeOfExamType } from '../../config/assessmentFamilies';
import { campaignAPI, examSetAPI, sessionAPI } from '../../services/api';
import { fmtDuration, resultDateText, inviteLinkOf, hasReport } from './derive';
import { batchNameOf } from './batches';
import { copyText, resetCandidateSession, sendSingleInvite } from './actions';
import { ResultActions } from './ResultRow';
import ReportModal from './ReportModal';
import RecordingModal from './RecordingModal';
import AiScreenedPanel from './AiScreenedPanel';

const STAGE_ORDER = ['review', 'shortlisted', 'notstarted'];

/** Prototype candDetail(): the expanded row. */
function CandidateDetail({ p, data, act, busyId, onShortlist, onAssign }) {
  const [assignOpen, setAssignOpen] = useState(false);
  const withBatch = true;
  const assigned = data.examSets.filter((es) => p.results[es.id]);
  const unassignedTests = data.examSets.filter((es) => !p.results[es.id]);
  return (
    <div className="wz-cdetail">
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div className={`wz-drow th${withBatch ? ' wb' : ''}`}>
          <span>Assessment</span>{withBatch && <span>Batch</span>}<span>Status</span><span>Score</span><span>Date</span><span>Time</span><span />
        </div>
        {assigned.map((es) => {
          const r = p.results[es.id];
          const t = typeOfExamType(es.exam_type);
          return (
            <div className={`wz-drow${withBatch ? ' wb' : ''}`} key={es.id}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <FamilyIcon fam={t.fam} size="m" />
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{es.name}</span>
                  <span className="wz-sub" style={{ fontSize: 12 }}>{t.name}</span>
                </span>
              </span>
              {withBatch && <span><BatchPill name={batchNameOf(data.batchesById, r.batchId)} /></span>}
              <ResultStatus state={r.state} interrupted={r.interrupted} />
              {r.state === 'done' ? <ScoreChip result={r} style={{ justifySelf: 'start' }} /> : <span style={{ color: 'var(--wz-ghost)' }}>—</span>}
              <span style={{ color: 'var(--wz-ink-2)' }}>{resultDateText(r)}</span>
              <span style={{ color: 'var(--wz-ink-2)' }}>{fmtDuration(r.durationSeconds)}</span>
              <ResultActions r={r} examType={es.exam_type} act={act} busyId={busyId} person={p} examSet={es} />
            </div>
          );
        })}
        {p.unassigned.length > 0 && (
          <p className="wz-sub" style={{ padding: '10px 0' }}>
            {assigned.length ? 'Also on this position without an interview (Unassigned).' : 'Not assigned to an interview yet (Unassigned).'}
          </p>
        )}
        {unassignedTests.length > 0 && (
          <div style={{ alignSelf: 'flex-start', marginTop: 12 }}>
            <Menu
              open={assignOpen}
              onClose={() => setAssignOpen(false)}
              align="left"
              trigger={<Button need="exam.manage" small icon="plus" aria-haspopup="true" aria-expanded={assignOpen} onClick={() => setAssignOpen((v) => !v)}>Assign another interview</Button>}
            >
              {unassignedTests.map((es) => (
                <MenuItem key={es.id} title={es.name} hint={typeOfExamType(es.exam_type).name} onClick={() => { setAssignOpen(false); onAssign(p, es); }} />
              ))}
            </Menu>
          </div>
        )}
      </div>
      <div className="wz-decide">
        <span className="wz-sub" style={{ fontSize: 12 }}>Recruiter</span>
        <span style={{ fontWeight: 500, marginTop: -6 }}>{p.recruiter || '—'}</span>
        <span className="wz-sub" style={{ fontSize: 12, marginTop: 6 }}>Decision</span>
        <Button need="candidate.status" variant="primary" icon="check" onClick={() => onShortlist(p)} aria-pressed={p.shortlisted}>
          {p.shortlisted ? 'Shortlisted' : 'Shortlist'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Report + recording buttons on the candidate's own row (no need to expand):
 * Report is the combined report with 2+ completed exams (grouped by email, like
 * groupPeople), else the one exam's report;
 * Recording opens the one recording, or a menu when several exams have one.
 */
function PersonActions({ p, examSets, act }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const done = p.all.filter((r) => hasReport(r.session));
  const recorded = examSets
    .map((es) => ({ es, r: p.results[es.id] }))
    .filter(({ r }) => r?.session?.has_recording);

  const openReport = () => (done.length >= 2 ? act.combined(p) : act.report(done[0]));
  const reportTitle = done.length >= 2 ? `Combined report · ${done.length} interviews`
    : done.length ? 'Interview report' : 'No completed interview yet';
  const recTitle = recorded.length ? 'Recording with proctoring flags' : 'No recording available';

  return (
    <span className="wz-pacts" onClick={(e) => e.stopPropagation()}>
      <Button iconOnly icon="file" disabled={!done.length} title={reportTitle} aria-label={reportTitle} onClick={openReport} />
      {recorded.length > 1 ? (
        <Menu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          trigger={<Button iconOnly icon="video" title={recTitle} aria-label={recTitle} aria-haspopup="true"
            aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)} />}
        >
          {recorded.map(({ es, r }) => (
            <MenuItem key={es.id} title={es.name} hint={resultDateText(r)}
              onClick={() => { setMenuOpen(false); act.recording(r, p, es); }} />
          ))}
        </Menu>
      ) : (
        <Button iconOnly icon="video" disabled={!recorded.length} title={recTitle} aria-label={recTitle}
          onClick={() => act.recording(recorded[0].r, p, recorded[0].es)} />
      )}
    </span>
  );
}

/** Resume / invite badges under the email (T0 item C). */
function PersonBadges({ p }) {
  const parsed = p.rows.some((r) => r.resume_parsed_at);
  const parsing = !parsed && p.rows.some((r) => r.resume_file_path);
  const invited = p.rows.some((r) => r.invite_sent_at || ['invited', 'started', 'completed'].includes(String(r.invite_status || '').toLowerCase()));
  if (!parsed && !parsing && !invited) return null;
  return (
    <span className="wz-badges">
      {parsed && <span className="wz-tag ok" title="Resume parsed — personalized questions ready">Resume ready</span>}
      {parsing && <span className="wz-tag warn" title="Resume uploaded but not yet parsed">Parsing résumé</span>}
      {invited && <span className="wz-tag ai">Invite sent</span>}
    </span>
  );
}

/**
 * Prototype viewCandidates(): toolbar + one of three views. "Candidate" view here;
 * Interview / Batch views come from `renderInterviewView` / `renderBatchView`.
 */
export default function CandidatesTab({
  campaign, data, hireBar, view, setView, ivFilter, setIvFilter, openKey, setOpenKey,
  renderInterviewView, renderBatchView,
}) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('all');
  const [sort, setSort] = useState('avg');
  const [hidden, setHidden] = useState(() => new Set());
  const [colsOpen, setColsOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [report, setReport] = useState(null);
  const [recording, setRecording] = useState(null);
  const batches = true;

  const q = search.trim().toLowerCase();
  const match = (p) => (stage === 'all' || p.stage === stage) && (!q || `${p.name} ${p.email}`.toLowerCase().includes(q));
  const stageCount = (k) => data.people.filter((p) => k === 'all' || p.stage === k).length;
  const hasUnassigned = data.people.some((p) => p.unassigned.length);

  const act = {
    copy: async (row) => {
      const link = inviteLinkOf(row);
      if (link && await copyText(link)) toast('Exam link copied');
    },
    report: (r) => setReport({ sessionId: r.session.id }),
    combined: (p) => setReport({ campaignId: campaign.id, email: p.email, name: p.name }),
    recording: (r, p, es) => setRecording({
      sessionId: r.session.id,
      name: p?.name || r.row?.name,
      subtitle: [es?.name, resultDateText(r)].filter(Boolean).join(' · '),
    }),
    remind: async (row) => {
      setBusyId(row.id);
      try {
        await sendSingleInvite(row.id);
        toast(String(row.invite_status || 'pending').toLowerCase() === 'pending' ? 'Invite sent.' : `Reminder sent to ${row.name || row.email}`);
        data.reloadAssignments();
      } catch (err) {
        toast(err.message || 'Failed to send invite.');
      } finally {
        setBusyId(null);
      }
    },
    resumeEmail: async (r) => {
      setBusyId(r.row.id);
      try {
        const res = await sessionAPI.sendResumeEmail(r.session.id);
        toast(`Continue link sent to ${res.data?.email || r.row.email}`);
        data.reloadAssignments();
      } catch (err) {
        toast(err.response?.data?.detail || 'Could not send the email.');
      } finally {
        setBusyId(null);
      }
    },
    reset: async (row) => {
      if (!window.confirm("Reset this candidate's session? Answers so far are kept; they get a fresh allowance of resumes and continue from the next question.")) return;
      try {
        await resetCandidateSession(row.id);
        toast('Session reset — candidate can restart.');
        data.reloadAssignments();
      } catch (err) {
        toast(err.message);
      }
    },
  };

  const onShortlist = async (p) => {
    const next = !p.shortlisted;
    const ids = p.rows.filter((r) => !String(r.id).startsWith('synced-')).map((r) => r.id);
    if (!ids.length) { toast('Import this candidate into an interview before shortlisting.'); return; }
    data.setCandidates((prev) => prev.map((c) => (ids.includes(c.id) ? { ...c, is_shortlisted: next } : c)));
    try {
      await Promise.all(ids.map((cid) => campaignAPI.setShortlisted(campaign.id, cid, next)));
      toast(`${p.name}: ${next ? 'Shortlisted' : 'Needs review'}`);
    } catch {
      data.setCandidates((prev) => prev.map((c) => (ids.includes(c.id) ? { ...c, is_shortlisted: !next } : c)));
      toast('Failed to update shortlist.');
    }
  };

  const onAssign = async (p, es) => {
    const native = p.rows.filter((r) => !String(r.id).startsWith('synced-'));
    const row = native.find((r) => !r.exam_set_id) || native[0];
    if (!row) { toast('Add this candidate from the interview’s “Add candidates” dialog.'); return; }
    try {
      await examSetAPI.assignCandidates(campaign.id, es.id, [row.id]);
      toast(`${p.name} assigned to ${es.name}`);
      data.reloadAssignments();
    } catch (err) {
      toast(err.response?.data?.detail || 'Failed to assign.');
    }
  };

  /* ---------- toolbar ---------- */
  const views = [['candidate', 'Candidate'], ['interview', 'Interview'], ...(batches ? [['batch', 'Batch']] : [])];
  const toolbar = (
    <>
      <div className="wz-toolbar">
        <div className="wz-viewsw">
          <span className="wz-sub">View by</span>
          <SegmentedControl label="View by" value={view} onChange={(v) => { setView(v); setColsOpen(false); }} options={views.map(([value, label]) => ({ value, label }))} />
        </div>
        <div className="right">
          <SearchInput label="Search candidates" placeholder="Search name or email" value={search} onChange={setSearch} />
          {view !== 'batch' && (
            <select aria-label="Filter by interview" className="wz-select" value={ivFilter} onChange={(e) => setIvFilter(e.target.value)}>
              <option value="all">All interviews</option>
              {data.examSets.map((es) => <option key={es.id} value={es.id}>{es.name}</option>)}
              {hasUnassigned && <option value="unassigned">Unassigned</option>}
            </select>
          )}
          {view === 'candidate' && (
            <Menu
              open={colsOpen}
              onClose={() => setColsOpen(false)}
              minWidth={240}
              trigger={<Button icon="columns" aria-haspopup="true" aria-expanded={colsOpen} onClick={() => setColsOpen((v) => !v)}>Columns</Button>}
            >
              <div className="wz-colmenu">
                {data.examSets.map((es) => (
                  <label key={es.id}>
                    <input type="checkbox" checked={!hidden.has(es.id)} onChange={() => setHidden((prev) => { const n = new Set(prev); if (n.has(es.id)) n.delete(es.id); else n.add(es.id); return n; })} />
                    <FamilyIcon fam={typeOfExamType(es.exam_type).fam} size="s" />
                    <span>{es.name}</span>
                  </label>
                ))}
              </div>
            </Menu>
          )}
        </div>
      </div>
      <div className="wz-toolbar">
        <SegmentedControl
          label="Filter by stage"
          value={stage}
          onChange={setStage}
          options={[{ value: 'all', label: 'All', count: stageCount('all') }, ...STAGE_ORDER.map((k) => ({ value: k, label: STAGES[k], count: stageCount(k) }))]}
        />
        {view === 'candidate' && (
          <select aria-label="Sort" className="wz-select" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="avg">Sort: Average score</option>
            <option value="name">Sort: Name</option>
          </select>
        )}
      </div>
    </>
  );

  const legend = (
    <div className="wz-legend" style={{ fontSize: 13, marginTop: 4 }}>
      <span><span className="wz-chip pass" style={{ minWidth: 28, height: 16, padding: 0 }} />At or above hire bar</span>
      <span><span className="wz-chip below" style={{ minWidth: 28, height: 16, padding: 0 }} />Below hire bar</span>
      <span><span className="wz-chip scoring" style={{ minWidth: 28, height: 16, padding: 0 }} />Scoring</span>
      <span><span className="wz-chip invited" style={{ minWidth: 28, height: 16, padding: 0 }} />Not started</span>
      <span>— Not assigned</span>
    </div>
  );

  /* ---------- view 1: one row per candidate ---------- */
  const people = useMemo(() => {
    const list = data.people.filter((p) => match(p) && (ivFilter === 'all'
      || (ivFilter === 'unassigned' ? p.unassigned.length > 0 : Boolean(p.results[ivFilter]))));
    return list.slice().sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (a.shortlisted !== b.shortlisted) return a.shortlisted ? -1 : 1; // T0: shortlisted first
      return (b.avg ?? -1) - (a.avg ?? -1) || a.name.localeCompare(b.name);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.people, stage, q, ivFilter, sort]);

  // Hidden columns never drop people: they only hide the column.
  const ivs = data.examSets.filter((es) => !hidden.has(es.id));

  const candidateView = (
    <Panel style={{ overflowX: 'auto' }}>
      <div className="wz-ctable" style={{ '--cols': ivs.length }}>
        <div className="wz-crow th">
          <span>Candidate</span>
          {ivs.map((es) => {
            const f = typeOfExamType(es.exam_type).fam;
            return (
              <span className="wz-colh" key={es.id}>
                <span className="f" style={{ color: `var(--wz-fam-${f})` }}><i style={{ background: `var(--wz-fam-${f})` }} />{FAMILIES[f].label}</span>
                <span className="n">{es.name}</span>
              </span>
            );
          })}
          <span>Average</span><span>Stage</span>
          <span className="wz-pacts wz-pacts-h"><span>Report</span><span>Recording</span></span><span />
        </div>
        {data.candidatesLoading ? <p className="wz-sub" style={{ padding: '24px 20px' }}>Loading candidates…</p>
          : people.length ? people.map((p) => {
            const open = openKey === p.key;
            return (
              <div className={`wz-cwrap${open ? ' open' : ''}`} key={p.key}>
                <div className="wz-crow">
                  <button type="button" className="wz-rowlink" onClick={() => setOpenKey(open ? null : p.key)}>
                    <Avatar name={p.name} />
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span className="wz-name">{p.name}</span>
                      <span className="wz-sub wz-ellipsis" style={{ fontSize: 12 }}>{p.email}</span>
                      <PersonBadges p={p} />
                    </span>
                  </button>
                  {ivs.map((es) => {
                    const r = p.results[es.id];
                    if (!r) return <ScoreChip key={es.id} result={null} />;
                    return (
                      <span key={es.id} style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                        <ScoreChip result={r} />
                        {batches && <span className="wz-sub" style={{ fontSize: 11, paddingLeft: 2 }}>{batchNameOf(data.batchesById, r.batchId)}</span>}
                      </span>
                    );
                  })}
                  <b style={{ fontSize: 15, color: p.avg == null ? 'var(--wz-ghost)' : scoreTone(p.avg, p.hireBar ?? hireBar) === 'pass' ? 'var(--wz-pass)' : 'var(--wz-below-fg)' }}>{fmtScore(p.avg)}</b>
                  <span><StagePill stage={p.stage} /></span>
                  <PersonActions p={p} examSets={data.examSets} act={act} />
                  <button type="button" className="wz-expand" aria-expanded={open} aria-label={`Show assessments for ${p.name}`} onClick={() => setOpenKey(open ? null : p.key)}>
                    <Icon name="chevD" />
                  </button>
                </div>
                {open && <CandidateDetail p={p} data={data} act={act} busyId={busyId} onShortlist={onShortlist} onAssign={onAssign} />}
              </div>
            );
          }) : <p className="wz-sub" style={{ padding: '24px 20px' }}>{data.people.length ? 'No candidates match these filters.' : 'No candidates yet. Add candidates from an interview.'}</p>}
        <div className="wz-sub" style={{ padding: '14px 20px', textAlign: 'right' }}>Showing {people.length} of {data.people.length}</div>
      </div>
    </Panel>
  );

  const ctx = { match, act, busyId, ivFilter, search: q, stage };
  let body = candidateView;
  if (view === 'interview') body = renderInterviewView ? renderInterviewView(ctx) : null;
  if (view === 'batch' && batches) body = renderBatchView ? renderBatchView(ctx) : null;

  return (
    <>
      <AiScreenedPanel campaignId={campaign.id} onInvited={data.reloadAssignments} />
      {toolbar}
      {body}
      {legend}
      {report && <ReportModal {...report} onClose={() => setReport(null)} />}
      {recording && <RecordingModal {...recording} onClose={() => setRecording(null)} />}
    </>
  );
}
